import Box from "@mui/material/Box/Box";
import Checkbox from "@mui/material/Checkbox/Checkbox";
import TableCell from "@mui/material/TableCell/TableCell";
import TableHead from "@mui/material/TableHead/TableHead";
import TableRow from "@mui/material/TableRow/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import { TABLE_HEADER_CELL_SX } from "../../../../components/ui";
import { HeadCell, HeadCellsType } from "./LeadsTableConstants";

interface EnhancedTableProps {
  headCells: HeadCell[];
  numSelected?: number;
  onRequestSort?: (event: React.MouseEvent<unknown>, newOrderBy: any) => void;
  onSelectAllClick?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  displaySelectAll: boolean;
  sortAsc?: boolean;
  sortBy?: string;
  rowCount?: number;
  removeActions?: boolean;
  /** Dark LLO-style header cells (used by legacy full TableHead consumers). */
  styled?: boolean;
}

export default function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    onSelectAllClick,
    sortAsc,
    sortBy,
    numSelected,
    rowCount,
    onRequestSort,
    styled = false,
  } = props;
  const createSortHandler =
    (newOrderBy: HeadCellsType) =>
    (event: React.MouseEvent<unknown>) => {
      if (!!onRequestSort) onRequestSort(event, newOrderBy);
    };
  const cellSx = styled ? TABLE_HEADER_CELL_SX : undefined;

  return (
    <TableHead>
      <TableRow>
        {props.displaySelectAll && (
          <TableCell padding="checkbox" sx={cellSx}>
            <Checkbox
              color="primary"
              sx={
                styled
                  ? {
                      color: "rgba(255,255,255,0.7)",
                      "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                        color: "#fff",
                      },
                    }
                  : undefined
              }
              indeterminate={
                !!numSelected &&
                numSelected > 0 &&
                numSelected < (rowCount ?? 0)
              }
              checked={(rowCount ?? 0) > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
            />
          </TableCell>
        )}
        {props.headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            padding={headCell.disablePadding ? "none" : "normal"}
            sx={cellSx}
          >
            {headCell.disableSorting ? (
              <>{headCell.label}</>
            ) : (
              <TableSortLabel
                active={sortBy === headCell.id}
                direction={
                  sortBy === headCell.id ? (sortAsc ? "asc" : "desc") : "asc"
                }
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {sortBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {sortAsc ? "sorted ascending" : "sorted descending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
        {!props.removeActions && (
          <>
            <TableCell align="left" sx={cellSx}></TableCell>
            <TableCell align="left" sx={cellSx}>
              Actions
            </TableCell>
          </>
        )}
      </TableRow>
    </TableHead>
  );
}

export function LeadsSelectAllHeadCell(props: {
  numSelected?: number;
  rowCount?: number;
  onSelectAllClick?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const { onSelectAllClick, numSelected, rowCount } = props;
  return (
    <TableCell padding="checkbox" sx={TABLE_HEADER_CELL_SX}>
      <Checkbox
        color="primary"
        sx={{
          color: "rgba(255,255,255,0.7)",
          "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#fff" },
        }}
        indeterminate={
          !!numSelected &&
          numSelected > 0 &&
          numSelected < (rowCount ?? 0)
        }
        checked={(rowCount ?? 0) > 0 && numSelected === rowCount}
        onChange={onSelectAllClick}
      />
    </TableCell>
  );
}

export function LeadsActionsHeadCells() {
  return (
    <>
      <TableCell align="left" sx={TABLE_HEADER_CELL_SX} />
      <TableCell align="left" sx={TABLE_HEADER_CELL_SX}>
        Actions
      </TableCell>
    </>
  );
}

export function mapLeadHeadCells(headCells: HeadCell[]) {
  return headCells.map((headCell) => ({
    id: String(headCell.id),
    label: headCell.label,
    sortable: !headCell.disableSorting,
    disablePadding: headCell.disablePadding,
  }));
}
